import React, { useState, useEffect, useCallback } from 'react';

import {
  CenteredContainer,
  DialogPrompt,
  LoadingSkeleton,
} from '../../components';
import * as Styled from './styled';
import * as GStyled from '../../styles/components';
import { StyledText, Button } from '@leasequery/lq.react-component-library';
import { ArchivedReport } from '../../models';
import { deleteArchivedReport, fetchReportArchives } from '../../api';
import ArchiveReportsTable from './archive-reports-table/ArchiveReportsTable';
import { useRouter } from 'next/router';
import { useAuth } from '../../context';

function ReportArchives(): JSX.Element {
  const { user } = useAuth();
  const router = useRouter();
  const [reportArchives, setReportArchives] = useState<ArchivedReport[]>(null);
  const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useState(false);
  const [isDeleteDialogDisabled, setIsDeleteDialogDisabled] = useState(false);
  const [deleteItemData, setDeleteItemData] = useState<ArchivedReport>();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const resp = await fetchReportArchives();
      setReportArchives(resp);
    };
    fetchData();
  }, []);

  const viewRecentReportsClickHandler = useCallback(() => {
    router.push('/results');
  }, [router]);

  const deleteItemClickHandler = useCallback(
    (archivedReport: ArchivedReport): void => {
      setIsDeleteDialogDisabled(false);
      setIsDeleteDialogOpened(true);
      setDeleteItemData(archivedReport);
    },
    [],
  );

  const deleteButtonClickHandler = useCallback(async () => {
    setIsDeleteDialogDisabled(true);

    await deleteArchivedReport(deleteItemData.backgroundTaskId);

    setIsDeleteDialogDisabled(false);
    setIsDeleteDialogOpened(false);
    // remove unarchived report form archived list
    const nextReportArchives = reportArchives.filter(
      (report) => report.backgroundTaskId !== deleteItemData.backgroundTaskId,
    );
    setReportArchives(nextReportArchives);
  }, [deleteItemData, reportArchives]);

  const cancelButtonClickHandler = useCallback(() => {
    setIsDeleteDialogOpened(false);
  }, []);

  return (
    <CenteredContainer>
      <Styled.PageHeaderWrapper>
        <GStyled.PageHeader>Archive for {user.firstName}</GStyled.PageHeader>
        <StyledText as="div">
          All archived report results will be stored indefintely unless deleted.
        </StyledText>
        <div>
          <Button onClick={viewRecentReportsClickHandler}>
            View Recent Reports
          </Button>
        </div>
      </Styled.PageHeaderWrapper>
      <Styled.Section>
        <Styled.ReportArchivesTitleBar>
          <StyledText fontWeight="bold" size="xl">
            Archived Reports
          </StyledText>
        </Styled.ReportArchivesTitleBar>
        {!reportArchives ? (
          <LoadingSkeleton name="table" />
        ) : (
          <ArchiveReportsTable
            reportResults={reportArchives}
            onDeleteButtonClick={deleteItemClickHandler}
          />
        )}
      </Styled.Section>
      <DialogPrompt
        isOpen={isDeleteDialogOpened}
        disabled={isDeleteDialogDisabled}
        title="Are you sure?"
        labels={{
          confirmButton: 'Delete',
        }}
        onCancel={cancelButtonClickHandler}
        onConfirm={deleteButtonClickHandler}
      >
        You are about to delete this report from your archive. This can not be
        undone.
      </DialogPrompt>
    </CenteredContainer>
  );
}

export default ReportArchives;
